import { useProfile } from '@gm-commercial/profile-context';
import { Role } from '@gm-commercial/profile-model';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import useIsInWebView from '~/common/hooks/useIsInWebView';
import { AppRoutePaths } from '~/common/models/route.model';
import {
  getHeaderDisabledPaths,
  getNavDisabledPaths,
  isImmersiveNavPath,
} from '~/common/utils/route/route.utils';
import FeedbackModal from '~/features/feedback/FeedbackModal';
import { useFeedbackLoopEnabled } from '~/features/feedback/useFeedbackLoopEnabled';
import ScopedUserBanner from '~/features/header/components/scopedUserBanner/ScopedUserBanner';
import GlobalHeader from '~/features/header/GlobalHeader';
import GmfAdvisorHeader from '~/features/header/GmfAdvisorHeader';
import GlobalNavDrawer, {
  DrawerHeader,
} from '~/features/navigationDrawer/GlobalNavDrawer';
import GmfAdvisorNav from '~/features/navigationDrawer/GmfAdvisorNav';
import { useSidekickContentInset } from '~/hooks/useSidekickContentInset';
import {
  NAV_DRAWER_LARGE_UP_CLOSED_WIDTH,
  NAV_DRAWER_OPEN_WIDTH,
} from './GlobalNavDrawer.styles';
import OnStarContentBanner from './OnStarContentBanner';

const GlobalNavWrapper = ({
  children,
}: {
  children?: ReactNode | undefined;
}): JSX.Element => {
  const { pathname } = useLocation();
  const { isInWebView } = useIsInWebView();
  const isFeedbackLoopEnabled = useFeedbackLoopEnabled();
  const [isFeedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [feedbackDraftMessage, setFeedbackDraftMessage] = useState('');
  const { profile } = useProfile();
  const isMinimalNav = !profile?.role;
  const isGmfAdvisor = profile?.role === Role.GMF_ADVISOR;
  const showFeedback = !isMinimalNav && !isGmfAdvisor && isFeedbackLoopEnabled;

  const theme = useTheme();
  const defaultExpanded = useMediaQuery(theme.breakpoints.up('lg'));
  // On mobile the scoped-user banner is rendered here in the page content
  // (below the OnStar banner) instead of inside the header.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navCoversContent = !defaultExpanded;
  const isImmersiveView = isImmersiveNavPath(pathname);
  const preventNavCollapse = isImmersiveView && !navCoversContent;

  const [navExpanded, setNavExpanded] = useState(
    defaultExpanded || preventNavCollapse
  );

  const toggleDrawer = useCallback(() => {
    if (isMinimalNav) {
      return;
    }
    setNavExpanded((previousValue) => !previousValue);
  }, [isMinimalNav]);
  const closeNav = () => setNavExpanded(false);

  // Close drawer on navigation if it obscures content while open
  const onNavItemClick = navCoversContent ? closeNav : undefined;

  // Certain content-only or flow-specific views should not display global nav components
  const disableNavDrawer = getNavDisabledPaths().some((path) =>
    matchPath(path, pathname)
  );
  const disableHeader = getHeaderDisabledPaths().some((path) =>
    matchPath(path, pathname)
  );
  const isMobileAppRequired = !!matchPath(
    AppRoutePaths.MOBILE_APP_REQUIRED,
    pathname
  );
  const showGlobalHeader =
    !disableHeader && !isInWebView && (!!profile || isMobileAppRequired);

  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname re-evaluates nav collapse on each navigation; it is a trigger, not read in the body
  useEffect(() => {
    if (isMinimalNav) {
      return;
    }

    setNavExpanded((currentlyExpanded) => {
      if (preventNavCollapse) {
        return true;
      }

      if (currentlyExpanded && navCoversContent) {
        return false;
      }
      return currentlyExpanded;
    });
  }, [pathname, preventNavCollapse, navCoversContent, isMinimalNav]);

  const ContentSpacer = useMemo(() => {
    const Spacer = () => <>{!disableHeader && <DrawerHeader />}</>;
    return Spacer;
  }, [disableHeader]);

  // Adjust content width based on nav state when presented side-by-side
  const showNavSideBySide =
    !disableNavDrawer && !navCoversContent && !isInWebView && !isMinimalNav;
  const collapsedNavWidth = navExpanded
    ? NAV_DRAWER_OPEN_WIDTH
    : NAV_DRAWER_LARGE_UP_CLOSED_WIDTH;
  const navWidth = showNavSideBySide ? collapsedNavWidth : 0;
  // The Sidekick drawer takes the opposite edge. Both insets fold into one
  // width so the two never compete for the same pixels.
  const sidekickWidth = useSidekickContentInset();
  const contentWidth = `calc(100% - ${navWidth + sidekickWidth}px)`;

  return (
    <>
      {!disableNavDrawer && !isInWebView && showFeedback && (
        <FeedbackModal
          isOpen={isFeedbackModalOpen}
          onClose={() => setFeedbackModalOpen(false)}
          draftMessage={feedbackDraftMessage}
          onDraftChange={setFeedbackDraftMessage}
          onSubmitSuccess={() => setFeedbackDraftMessage('')}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          width: '100vw',
          overflowX: 'hidden',
        }}
      >
        {!disableNavDrawer &&
          !isInWebView &&
          (isGmfAdvisor || isMinimalNav ? (
            <GmfAdvisorNav
              expanded={navExpanded}
              onToggle={toggleDrawer}
              resolved={isGmfAdvisor}
            />
          ) : (
            <GlobalNavDrawer
              navExpanded={navExpanded}
              toggleDrawer={toggleDrawer}
              onNavItemClick={onNavItemClick}
              toggleable={!preventNavCollapse}
              isMinimal={isMinimalNav}
              feedbackNavAction={
                showFeedback
                  ? { onOpen: () => setFeedbackModalOpen(true) }
                  : undefined
              }
            />
          ))}
        {showGlobalHeader &&
          !isGmfAdvisor &&
          (!isMinimalNav || isMobileAppRequired) && (
            <GlobalHeader
              navExpanded={navExpanded}
              navDrawerHidden={disableNavDrawer}
            />
          )}
        {showGlobalHeader && isGmfAdvisor && (
          <GmfAdvisorHeader navExpanded={navExpanded} profile={profile} />
        )}
        <Box
          component="main"
          width={contentWidth}
          sx={(theme) => ({
            transition: theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: navExpanded
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
            overflowX: 'hidden',
            minWidth: 0,
          })}
        >
          {!isInWebView && <ContentSpacer />}
          {profile &&
            !isGmfAdvisor &&
            !disableNavDrawer &&
            !defaultExpanded && <OnStarContentBanner />}
          {profile &&
            !isGmfAdvisor &&
            !disableNavDrawer &&
            !isInWebView &&
            isMobile && <ScopedUserBanner />}

          {children}
        </Box>
      </Box>
    </>
  );
};

export default GlobalNavWrapper;
